import React, { useCallback, useState, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, Grid, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

// Wrapper component to replace material-ui-dropzone DropzoneArea
// This maintains similar API for backward compatibility
export const DropzoneArea = ({ 
  onChange, 
  maxFileSize = 5000000, 
  filesLimit = 10,
  acceptedFiles = [],
  className,
  showAlerts = false,
  dropzoneText = "Dosyaları buraya sürükleyin veya tıklayın",
  ...props 
}) => {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);

  useEffect(() => {
    // Clean up previews on unmount
    return () => {
      previews.forEach(preview => {
        if (preview.url && preview.url.startsWith('blob:')) {
          URL.revokeObjectURL(preview.url);
        }
      });
    };
  }, [previews]);

  const onDrop = useCallback((acceptedFiles, rejectedFiles) => {
    if (showAlerts && rejectedFiles.length > 0) {
      const errors = rejectedFiles.map(file => {
        if (file.file?.size > maxFileSize || file.size > maxFileSize) {
          return `${file.file?.name || file.name}: Dosya çok büyük (Max: ${(maxFileSize / 1000000).toFixed(0)}MB)`;
        }
        return `${file.file?.name || file.name}: Geçersiz dosya tipi`;
      });
      alert(errors.join('\n'));
    }

    const newFiles = [...files, ...acceptedFiles].slice(0, filesLimit);
    setFiles(newFiles);
    
    // Create previews
    const newPreviews = newFiles.map(file => {
      const isImage = file.type.startsWith('image/');
      return {
        file: file,
        url: isImage ? URL.createObjectURL(file) : null,
        isImage: isImage,
        isPdf: file.type === 'application/pdf',
      };
    });
    setPreviews(newPreviews);
    
    if (onChange) {
      onChange(newFiles);
    }
  }, [onChange, files, filesLimit, maxFileSize, showAlerts]);

  const handleDelete = (index) => {
    // Revoke URL for deleted preview
    if (previews[index]?.url) {
      URL.revokeObjectURL(previews[index].url);
    }
    
    const newFiles = files.filter((_, i) => i !== index);
    const newPreviews = previews.filter((_, i) => i !== index);
    
    setFiles(newFiles);
    setPreviews(newPreviews);
    
    if (onChange) {
      onChange(newFiles);
    }
  };

  // Convert acceptedFiles array to accept object for react-dropzone
  // Format: { 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'] }
  const acceptObject = acceptedFiles.length > 0 
    ? acceptedFiles.reduce((acc, type) => {
        if (type === 'image/jpeg') {
          acc['image/jpeg'] = ['.jpg', '.jpeg'];
        } else if (type === 'image/png') {
          acc['image/png'] = ['.png'];
        } else if (type === 'image/bmp') {
          acc['image/bmp'] = ['.bmp'];
        } else if (type === 'application/pdf') {
          acc['application/pdf'] = ['.pdf'];
        } else {
          acc[type] = [];
        }
        return acc;
      }, {})
    : undefined;

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    maxSize: maxFileSize,
    maxFiles: filesLimit,
    accept: acceptObject,
    multiple: filesLimit > 1,
  });

  return (
    <Box>
      <Box
        {...getRootProps()}
        className={className}
        sx={{
          border: isDragReject ? '2px dashed #f44336' : '2px dashed #aaa',
          borderRadius: 2,
          padding: 2,
          textAlign: 'center',
          cursor: 'pointer',
          backgroundColor: isDragActive ? (isDragReject ? '#ffebee' : '#e3f2fd') : '#fafafa',
          transition: 'all 0.2s ease',
          '&:hover': {
            backgroundColor: '#f5f5f5',
            borderColor: '#5e72e4',
          },
          minHeight: previews.length > 0 ? 'auto' : '150px',
        }}
      >
        <input {...getInputProps()} />
        
        {previews.length === 0 ? (
          <Box sx={{ padding: 4 }}>
            <Typography variant="body1" color="textSecondary">
              {isDragActive ? (
                isDragReject ? (
                  "Bu dosya tipi kabul edilmiyor"
                ) : (
                  "Dosyaları buraya bırakın..."
                )
              ) : (
                dropzoneText
              )}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ padding: 2 }}>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
              {dropzoneText}
            </Typography>
            <Grid container spacing={2}>
              {previews.map((preview, index) => (
                <Grid item xs={6} sm={4} md={3} key={index}>
                  <Box
                    sx={{
                      position: 'relative',
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      padding: 1,
                      backgroundColor: 'white',
                      '&:hover .delete-button': {
                        opacity: 1,
                      },
                    }}
                  >
                    <IconButton
                      className="delete-button"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(index);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 4,
                        right: 4,
                        backgroundColor: 'rgba(255, 255, 255, 0.9)',
                        opacity: 0,
                        transition: 'opacity 0.2s',
                        '&:hover': {
                          backgroundColor: '#f44336',
                          color: 'white',
                        },
                        zIndex: 1,
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    
                    {preview.isImage ? (
                      <img
                        src={preview.url}
                        alt={preview.file.name}
                        style={{
                          width: '100%',
                          height: '120px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                        }}
                      />
                    ) : preview.isPdf ? (
                      <Box
                        sx={{
                          width: '100%',
                          height: '120px',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                        }}
                      >
                        <InsertDriveFileIcon sx={{ fontSize: 48, color: '#d32f2f' }} />
                        <Typography variant="caption" sx={{ mt: 1 }}>
                          PDF
                        </Typography>
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: '120px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f5f5f5',
                          borderRadius: 1,
                        }}
                      >
                        <InsertDriveFileIcon sx={{ fontSize: 48, color: '#999' }} />
                      </Box>
                    )}
                    
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        mt: 1,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {preview.file.name}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {(preview.file.size / 1024).toFixed(2)} KB
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default DropzoneArea;


